import React, { useCallback, useEffect, useState } from "react";
import { BlockEditor, TiptapEditor } from "@panda0/panda0-editor";
import {
  Button,
  InfoTooltip,
  SimpleTooltipContent,
  Tooltip,
} from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import { motion } from "framer-motion";
import { Wand2, X } from "lucide-react";
import { toast } from "sonner";

import { UpgradeRequiredToast } from "@/ui/shared/upgrade-required-toast";

// Full-screen editor component
const FullScreenEditor = ({
  editor,
  setEditor,
  initialDescription,
  onClose,
  onGenerate,
  isGenerating,
  hasQuizGenerated,
  savedJobId,
  workspaceId,
  editorRef,
  isNew = false,
  lastGeneratedContent,
}) => {
  // Local state for content checking
  const [hasContent, setHasContent] = useState(false);
  const [hasSignificantChanges, setHasSignificantChanges] = useState(false);

  // Function to check if editor has content and determine if changes are significant
  const checkEditorContent = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.getHTML();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const text = tempDiv.textContent || tempDiv.innerText || "";

      // Check for minimum content length
      const hasEnoughContent = text.trim().length >= 100;

      // Check if content has changed significantly from last generated content
      if (lastGeneratedContent) {
        const tempLastDiv = document.createElement("div");
        tempLastDiv.innerHTML = lastGeneratedContent;
        const lastText = tempLastDiv.textContent || tempLastDiv.innerText || "";

        // Calculate how different the content is from last time
        // Simple approach: content length change of at least 15%
        const contentChangePercent =
          Math.abs(text.length - lastText.length) / lastText.length;
        const hasChanged = contentChangePercent > 0.15 || text.length === 0;

        setHasSignificantChanges(hasChanged && hasEnoughContent);
        setHasContent(hasEnoughContent);
        return {
          hasContent: hasEnoughContent,
          hasSignificantChanges: hasChanged && hasEnoughContent,
        };
      }

      setHasSignificantChanges(hasEnoughContent);
      setHasContent(hasEnoughContent);
      return {
        hasContent: hasEnoughContent,
        hasSignificantChanges: hasEnoughContent,
      };
    }
    return {
      hasContent: false,
      hasSignificantChanges: false,
    };
  }, [editorRef, lastGeneratedContent]);

  // Check content on mount and when dependencies change
  useEffect(() => {
    checkEditorContent();
  }, [checkEditorContent]);

  // Generate function that captures the current state
  const handleGenerate = useCallback(() => {
    const result = checkEditorContent();
    if (!result.hasContent) {
      toast.error(
        "Please enter a job description with at least 100 characters"
      );
      return;
    }

    // Store the content we're generating from
    if (editorRef.current) {
      const html = editorRef.current.getHTML();
      if (html && typeof html === "string") {
        onGenerate(html);
      } else {
        toast.error("Could not retrieve editor content");
      }
    }
  }, [checkEditorContent, editorRef, onGenerate]);

  return (
    <motion.div
      className={cn(
        "bg-white overflow-hidden flex flex-col",
        isNew ? "fixed inset-0 z-50" : "h-full"
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 flex justify-between items-center">
        <h2 className="text-lg font-medium flex items-center gap-2">
          Job Description
          <InfoTooltip
            content={
              <SimpleTooltipContent
                title="Enter a detailed job description to automatically generate skill requirements."
                cta="Learn more about AI-powered skill extraction."
                href="#"
              />
            }
          />
        </h2>

        <div className="flex items-center gap-2">
          {hasQuizGenerated ? (
            <Tooltip content="Cannot regenerate skills after quiz has been generated">
              <div>
                <Button
                  variant="primary"
                  text="Generate Skills"
                  icon={<Wand2 className="w-4 h-4" />}
                  disabled={true}
                  className="h-9 w-fit opacity-50 cursor-not-allowed"
                />
              </div>
            </Tooltip>
          ) : (
            <Button
              variant="primary"
              onClick={handleGenerate}
              text="Generate Skills"
              icon={<Wand2 className="w-4 h-4" />}
              loading={isGenerating}
              disabled={
                isGenerating ||
                !savedJobId ||
                !hasContent ||
                (!isNew && !hasSignificantChanges && lastGeneratedContent)
              }
              className="h-9 w-fit"
              disabledTooltip={
                !hasContent
                  ? "Please enter a job description with at least 100 characters"
                  : !isNew && !hasSignificantChanges && lastGeneratedContent
                    ? "Make significant changes to the description to generate new skills"
                    : undefined
              }
            />
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            text={isNew ? "Close" : "Done"}
            icon={<X className="w-4 h-4" />}
            className="h-9 w-fit"
          />
        </div>
      </div>

      <motion.div
        className="p-4 flex-1 overflow-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <BlockEditor
          upgradeElement={
            <UpgradeRequiredToast
              title="You've exceeded your AI usage limit"
              message="Error generating response"
            />
          }
          placeholder="Paste job description or use ~ to open AI editor"
          onCreate={(editor) => {
            setEditor(editor);
            editorRef.current = editor;
            // Initial check after creation
            setTimeout(checkEditorContent, 100);
          }}
          onUpdate={(editor) => {
            setEditor(editor);
            editorRef.current = editor;
            // Update the content status whenever editor changes
            setTimeout(checkEditorContent, 100);
          }}
          api={`/api/v1/ai/completion?workspaceId=${workspaceId}`}
          appendToSelector=".modal-form"
          content={initialDescription}
          key="editor-instance"
        />
      </motion.div>
    </motion.div>
  );
};

export default React.memo(FullScreenEditor);
